import React from 'react'
import { capitalizeMyText } from '../../utils/helpers'
import GameCardFactory from '../../factory/GameCardFactory'
import { useSelector } from 'react-redux'
import Slider from 'components/Slider/Slider'
import Icon from 'icons/Icon'
import { SLIDER_VARIANT } from '../../constants/uiConfig'

/**
 * A component that renders either an icon or an image with fallback behavior
 * @component
 * @param {Object} props - The component props
 * @param {boolean} props.recent - Flag to determine if this is for recent items
 * @param {string} props.imageUrl - URL of the image to display
 * @returns {JSX.Element} Icon or image element with fallback handling
 */
function HeaderIconWithFallback ({ recent, imageUrl }) {
  const [showFallbackIcon, setShowFallbackIcon] = React.useState(false)
  if (recent) {
    return <Icon name='clock-rotate-left' className='type-icon' />
  }
  if (showFallbackIcon || !imageUrl) {
    return <Icon name='category-default' className='type-icon' />
  }
  return (
    <img
      alt='recent-games'
      className='type-icon'
      src={imageUrl}
      onError={() => setShowFallbackIcon(true)}
    />
  )
}

/**
 * Custom component to override carousel arrow buttons in the slider
 * @component
 * @param {Object} props - The component props
 * @param {string} props.className - CSS class name for styling
 * @param {Object} props.style - Inline styles for the arrow
 * @param {Function} props.onClick - Click handler for the arrow
 * @param {string} props.imgUrl - URL of the arrow image
 * @returns {JSX.Element} Custom arrow button for the slider
 */
function CustomSliderArrow (props) {
  const { className, style, onClick, imgUrl } = props
  return (
    <div
      className={`${className} d-flex`}
      style={{ ...style }}
      onClick={onClick}
    >
      <img src={imgUrl} alt='>' />
    </div>
  )
}

/**
 * Renders a slider component for displaying a collection of games with navigation and filtering options
 * @component
 * @param {Object} props - Component props
 * @param {string} props.categorySlug - Category slug used to build the games slider URL
 * @param {Array} props.games - Array of game objects to display in the slider
 * @param {Object|string} props.name - Name of the game section, can be localized object or string
 * @param {string} props.imageUrl - URL for the section header icon
 * @param {string} props.id - ID for the game subcategory
 * @param {boolean} [props.recent=false] - Flag to indicate if this is recent games section
 * @param {string} props.selectedLanguageCode - Current language code for localization
 * @param {Function} props.navigate - Navigation function from react-router
 * @param {Function} props.changeMenuTab - Function to handle menu tab changes
 * @param {Object} props.gameCategory - Game category data object
 * @param {Object} props.languageData - Localization data object
 * @param {Function} props.toggleFavorite - Function to toggle game favorite status
 * @param {boolean} [props.isLabeled=false] - Flag to indicate if games should show labels
 * @param {string} props.labelIconUrl - URL for the label icon
 * @returns {JSX.Element|null} The slider component or null if no games
 */
const GetSliderGames = ({
  categorySlug,
  games = [],
  name,
  slug,
  imageUrl,
  id,
  recent = false,
  selectedLanguageCode,
  navigate,
  // changeMenuTab,
  gameCategory,
  languageData,
  toggleFavorite,
  isLabeled = false,
  labelIconUrl,
  variant = SLIDER_VARIANT.V1,
  favoriteGamesList,
  setSelectedFilter = () => {}
}) => {
  const uiConfig = useSelector((state) => state?.uiConfig?.general)
  const { customCarouselArrows } = uiConfig || {}
  const hasCustomerSliderIcons = Boolean(customCarouselArrows)

  return (
    games?.length > 0 && (
      <div className='game-slider-container' key='list-container'>
        <Slider
          variant={variant}
          slides={games}
          nextArrow={hasCustomerSliderIcons ? <CustomSliderArrow imgUrl={customCarouselArrows?.nextIcon} /> : null}
          prevArrow={hasCustomerSliderIcons ? <CustomSliderArrow imgUrl={customCarouselArrows?.prevIcon} /> : null}
          showViewAllButton={games?.length > 0 && !recent}
          viewAllButtonText={languageData?.casinoGameViewAllBtn}
          headerIcon={() => <HeaderIconWithFallback recent={recent} imageUrl={imageUrl} />}
          headerTitle={capitalizeMyText(
            name?.[selectedLanguageCode] || name?.EN || name,
            '_'
          )}
          onClickViewAll={() => {
            setSelectedFilter(selectedFilter => ({ ...selectedFilter, pageNo: 1, subCategoryId: id, category: null }))
            navigate(
              `/${selectedLanguageCode?.toLowerCase()}/${categorySlug}/${slug}`,
              {
                state: {
                  from: gameCategory?.casinoCategories?.rows?.[0]
                    ?.id,
                  selectedMenu: id
                }
              }
            )
          }}
          cardProps={{ isLabeled, labelIconUrl }}
          sliderCard={({
            index,
            game,
            cardProps = {}
          }) => (
            <GameCardFactory
              id={id}
              lobby
              key={index}
              game={game}
              index={index}
              toggleFavorite={toggleFavorite}
              selectedLanguageCode={selectedLanguageCode}
              favoriteGamesList={favoriteGamesList}
              {...cardProps}
            />
          )}
        />
      </div>
    )
  )
}

export default GetSliderGames
